import { post, put, destroy, patch, get } from "@rails/request.js";

import { toast } from "sonner";
const API_URL = ""; // Replace with your actual API URL

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials extends LoginCredentials {
  confirmPassword: string;
}

export interface AuthResponse {
  token: string;
  user: {
    id: string;
    email: string;
  };
}

export interface StripeConnectStartResponse {
  redirect_url: string;
}

export async function loginUser(credentials: LoginCredentials): Promise<AuthResponse> {
  const response = await post(`${API_URL}/users/sign_in.json`, {
    body: JSON.stringify({ user: credentials }),
    responseKind: "json",
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to login");
  }

  return response.json;
}

export async function registerUser(credentials: RegisterCredentials): Promise<AuthResponse> {
  const { confirmPassword, ...registerData } = credentials;
  const response = await post(`${API_URL}/users.json`, {
    body: JSON.stringify(
      {
        user: registerData
      }
    ),
    responseKind: "json",
  });


  if (!response.ok) {
    const error = await response.json;
    if (error.errors) {
      // Throw an object with errors for form handling
      throw { errors: error.errors, message: "Failed to register" };
    }
    throw new Error(error.error || "Failed to register");
  }

  return response.json;
}

export async function requestPasswordReset(email: string): Promise<void> {
  const response = await put(`${API_URL}/users/password.json`, {
    body: JSON.stringify({ email }),
    responseKind: "json",
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to send reset instructions");
  }
}

export async function logoutUser(): Promise<void> {
  const response = await destroy(`${API_URL}/users/sign_out.json`, {
    responseKind: "json",
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to sign out");
  }
}

/**
 * Start Stripe Connect onboarding.
 * POST /stripe_connect.json
 */
export async function startStripeConnect(country?: string): Promise<StripeConnectStartResponse> {
  const response = await post(`/stripe_connect.json`, {
    responseKind: "json",
    body: JSON.stringify({ country }),
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to start Stripe Connect");
  }

  return await response.json;
}

/**
 * Fetch all sites.
 */
export async function getSites(): Promise<any[]> {
  const response = await get(`${API_URL}/sites.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch sites");
  }
  return await response.json;
}

/**
 * Fetch default themes for sites.
 * GET /sites/default_themes.json
 */
export async function getDefaultThemes(): Promise<{ id: number; name: string }[]> {
  const response = await get(`/sites/default_themes.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch default themes");
  }
  return await response.json;
}

/**
 * Fetch sections for a site.
 * @param siteSlug The slug of the site.
 * @returns Array of sections.
 */
export async function getSiteSections(siteSlug: string): Promise<any[]> {
  const response = await get(`/sites/${siteSlug}/sections.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch site sections");
  }
  return await response.json;
}


/**
 * Delete a project from a site.
 * DELETE /sites/:siteSlug/projects/:projectId.json
 */
export async function removeProject(siteSlug: string, projectId: number | string) {
  const response = await destroy(`/sites/${siteSlug}/projects/${projectId}.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to delete project");
  }
  return response.json;
}


/**
 * Delete a section from a site.
 * DELETE /sites/:siteSlug/sections/:sectionId.json
 */
export async function removeSection(siteSlug: string, sectionId: number | string) {
  const response = await destroy(`/sites/${siteSlug}/sections/${sectionId}.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to delete section");
  }
  return response.json;
}


/**
 * Sort sections for a site.
 * @param siteId The site ID.
 * @param sectionIds Array of section IDs in the new order.
 */
/**
 * Move a section to a new position.
 * PATCH /sites/:slug/sections/:sectionSlug/move
 * Payload: { id, parent_section_id, position }
 */
export async function moveSection(siteSlug: string, sectionSlug: string, position: number) {
  const response = await patch(`/sites/${siteSlug}/sections/${sectionSlug}/move`, {
    responseKind: "json",
    body: JSON.stringify({
      id: sectionSlug,
      parent_section_id: null,
      position,
    }),
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to move section");
  }
  const data = await response.json;

  toast("Project moved!");
  return data;
}

/**
 * Move a project to a new section/position.
 * PATCH /sites/:slug/sections/:secslug/projects/:projectslug/move
 * Payload: { section: { id, position } }
 */
export async function moveProject(
  siteSlug: string,
  sectionSlug: string,
  projectSlug: string,
  targetSectionSlug: string,
  position: number
) {
  const response = await patch(
    `/sites/${siteSlug}/sections/${sectionSlug}/projects/${projectSlug}/move`,
    {
      responseKind: "json",
      body: JSON.stringify({
        section: {
          parent_section_id: targetSectionSlug,
          position,
        },
      }),
    }
  );
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to move project");
  }

  const res = await response.json

  return res;
}

/**
 * List all photos for a project.
 */
export async function getProjectPhotos(siteSlug: string, projectId: number) {
  const response = await fetch(`/sites/${siteSlug}/projects/${projectId}/photos.json`, {
    credentials: "include",
    headers: { "Accept": "application/json" }
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to fetch project photos");
  }
  return response.json();
}

/**
 * Upload a photo to a project using a signed_id from ActiveStorage direct upload.
 */
export async function uploadProjectPhoto(siteSlug: string, projectId: number, signedId: string) {
  const formData = new FormData();
  formData.append("photo[file]", signedId);

  const response = await post(`/sites/${siteSlug}/projects/${projectId}/photos.json`, {
    body: formData,
    responseKind: "json",
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to upload photo");
  }
  return response.json;
}

/**
 * Delete a photo from a project.
 */
export async function deleteProjectPhoto(siteSlug: string, projectId: number, photoId: number) {
  const response = await destroy(`/sites/${siteSlug}/projects/${projectId}/photos/${photoId}.json`, {
    responseKind: "json",
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to delete photo");
  }
  return response.json;
}

/**
 * Update a photo's crop coordinates for a project.
 * PATCH /sites/:siteSlug/projects/:projectId/photos/:photoId.json
 * @param siteSlug The slug of the site.
 * @param projectId The project ID.
 * @param photoId The photo ID.
 * @param cropData { crop_x, crop_y, crop_width, crop_height }
 */
export async function updateProjectPhoto(
  siteSlug: string,
  projectId: number,
  photoId: number,
  cropData: { crop_x: number; crop_y: number; crop_width: number; crop_height: number }
) {
  const response = await patch(
    `/sites/${siteSlug}/projects/${projectId}/photos/${photoId}.json`,
    {
      body: JSON.stringify({ photo: cropData }),
      responseKind: "json",
      headers: { "Content-Type": "application/json" }
    }
  );
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to update photo");
  }
  return response.json;
}

/**
 * Fetch collaborators (site users) for a site.
 * @param siteSlug The slug of the site.
 * @returns Array of collaborators.
 */
export async function getSiteCollaborators(siteSlug: string): Promise<any[]> {
  const response = await get(`/sites/${siteSlug}/users.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch site collaborators");
  }
  return await response.json;
}

/**
 * Delete a collaborator from a site.
 * @param siteSlug The slug of the site.
 * @param collaboratorId The id of the site_user record.
 */
export async function deleteSiteCollaborator(siteSlug: string, collaboratorId: number) {
  const response = await destroy(`/sites/${siteSlug}/users/${collaboratorId}.json`, {
    responseKind: "json",
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to delete collaborator");
  }
  return response.json;
}

/**
 * Invite a collaborator to a site.
 * @param siteSlug The slug of the site.
 * @param email The email to invite.
 */
export async function inviteSiteCollaborator(siteSlug: string, email: string) {
  const response = await post(`/sites/${siteSlug}/users.json`, {
    body: JSON.stringify({ user: { email } }),
    responseKind: "json",
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to invite collaborator");
  }
  return response.json;
}

/**
 * Blog posts CRUD for a site.
 */
export async function getSitePosts(siteSlug: string) {
  const response = await get(`/sites/${siteSlug}/posts.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch posts");
  }
  return await response.json;
}

export async function createSitePost(siteSlug: string, data: any) {
  const response = await post(`/sites/${siteSlug}/posts.json`, {
    body: JSON.stringify({ post: data }),
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to create post");
  }
  return response.json;
}

export async function getSitePost(siteSlug: string, postSlug: string) {
  const response = await get(`/sites/${siteSlug}/posts/${postSlug}.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch post");
  }
  return await response.json;
}

export async function updateSitePost(siteSlug: string, postId: number | string, data: any) {
  const response = await patch(`/sites/${siteSlug}/posts/${postId}.json`, {
    body: JSON.stringify({ post: data }),
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to update post");
  }
  return response.json;
}

export async function deleteSitePost(siteSlug: string, postId: number) {
  const response = await destroy(`/sites/${siteSlug}/posts/${postId}.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to delete post");
  }
  return response.json;
}

/**
 * Fetch all authentications for the current user.
 * GET /authentications.json
 */
export async function getAuthentications(): Promise<any> {
  const response = await get(`/authentications.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch authentications");
  }
  return await response.json;
}

/**
 * Fetch themes data for a site.
 * GET /sites/:site_id/themes.json
 */
export async function getSiteThemes(siteId: number | string): Promise<any> {
  const response = await get(`/sites/${siteId}/themes.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch site themes");
  }
  return await response.json;
}

/**
 * Fetch a single theme for a site.
 * GET /sites/:site_id/themes/:id.json
 */
export async function getTheme(siteId: string, themeId: string): Promise<any> {
  const response = await get(`/sites/${siteId}/themes/${themeId}.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch theme");
  }
  return await response.json;
}

/**
 * Fetch available themes for quick start (system themes + site themes).
 * GET /sites/:site_id/themes/available.json
 */
export async function getAvailableThemes(siteId: string): Promise<any> {
  const response = await get(`/sites/${siteId}/themes/available.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch available themes");
  }
  return await response.json;
}

/**
 * Download a theme tarball and activate it.
 * POST /sites/:site_id/themes/:theme_id/download_tarball.json
 */
export async function downloadThemeTarball(siteId: string, themeId: string): Promise<any> {
  const response = await post(`/sites/${siteId}/themes/${themeId}/download_tarball.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to start theme download");
  }
  return await response.json;
}

/**
 * Fetch Vercel projects for a site.
 * GET /site/:slug/vercel_projects.json
 */
export async function getSiteVercelProjects(siteSlug: string): Promise<any[]> {
  const response = await get(`/sites/${siteSlug}/vercel_projects.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch Vercel projects");
  }
  return await response.json;
}

/**
 * Fetch repos for a site.
 * GET /sites/:slug/repos.json
 */
export async function getSiteRepos(siteSlug: string): Promise<any[]> {
  const response = await get(`/sites/${siteSlug}/repos.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch repos");
  }
  return await response.json;
}

/**
 * Push a theme build to GitHub by creating a new branch.
 * POST /sites/:site_id/themes/:theme_id/theme_builds/:id/push_to_github
 */
export async function pushThemeBuildToGithub(
  siteId: string,
  themeId: string,
  buildId: string,
  data: { repo: string; branch_name?: string; commit_message?: string }
): Promise<any> {
  const response = await post(`/sites/${siteId}/themes/${themeId}/theme_builds/${buildId}/push_to_github`, {
    body: JSON.stringify(data),
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to push to GitHub");
  }
  return await response.json;
}

/**
 * Update a theme for a site.
 * PATCH /sites/:site_id/themes/:id.json
 */
export async function updateTheme(siteId: string, themeId: string, data: any): Promise<any> {
  const response = await patch(`/sites/${siteId}/themes/${themeId}.json`, {
    body: JSON.stringify({ theme: data }),
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to update theme");
  }
  return await response.json;
}

/**
 * Delete an authentication by ID.
 * DELETE /authentications/:id.json
 */
export async function deleteAuthentication(id: number): Promise<any> {
  const response = await destroy(`/authentications/${id}.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to remove authentication");
  }
  return await response.json;
}

/**
 * Trigger a deployment for a site.
 * POST /sites/:slug/deployments
 */
export async function postSiteDeployment(siteSlug: string): Promise<any> {
  const response = await post(`/sites/${siteSlug}/deployments`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to trigger deployment");
  }
  return await response.json;
}

/**
 * Fetch a single site by slug.
 * GET /sites/:siteSlug.json
 */
export async function getSite(siteSlug: string): Promise<any> {
  const response = await get(`/sites/${siteSlug}.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch site");
  }
  return await response.json;
}

/**
 * Fetch a single site by slug.
 * GET /sites/:siteSlug.json
 */
export async function getSiteTarball(siteSlug: string): Promise<any> {
  const response = await get(`/sites/${siteSlug}/theme_tarball.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch site");
  }
  return await response.json;
}

export async function getSiteTarballJSON(siteSlug: string): Promise<any> {
  const response = await get(`/sites/${siteSlug}/theme_tarball_data.json`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch site");
  }
  return await response.json;
}

/**
 * Fetch oEmbed data for a given URL.
 * GET /oembed.json?url=...
 */
export async function getOEmbed(url: string): Promise<any> {
  const response = await get(`/oembed.json?url=${encodeURIComponent(url)}`);
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch oEmbed data");
  }
  return await response.json;
}

/**
 * List all project files for a project.
 */
export async function getProjectFiles(siteSlug: string, projectId: number) {
  const response = await fetch(`/sites/${siteSlug}/projects/${projectId}/project_files.json`, {
    credentials: "include",
    headers: { "Accept": "application/json" }
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to fetch project files");
  }
  return response.json();
}

/**
 * Upload a file to a project using a signed_id from ActiveStorage direct upload.
 */
export async function uploadProjectFile(
  siteSlug: string,
  projectId: number,
  signedId: string,
  title: string,
  description: string,
  fileType: string
) {
  const formData = new FormData();
  formData.append("project_file[file]", signedId);
  formData.append("project_file[title]", title);
  formData.append("project_file[description]", description);
  formData.append("project_file[file_type]", fileType);

  const response = await post(`/sites/${siteSlug}/projects/${projectId}/project_files.json`, {
    body: formData,
    responseKind: "json",
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to upload file");
  }
  return response.json;
}

/**
 * Delete a file from a project.
 */
export async function deleteProjectFile(siteSlug: string, projectId: number, fileId: number) {
  const response = await destroy(`/sites/${siteSlug}/projects/${projectId}/project_files/${fileId}.json`, {
    responseKind: "json",
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to delete file");
  }
  return response.json;
}

/**
 * Update a project file.
 */
export async function updateProjectFile(
  siteSlug: string,
  projectId: number,
  fileId: number,
  data: { title?: string; description?: string; file_type?: string }
) {
  const response = await patch(
    `/sites/${siteSlug}/projects/${projectId}/project_files/${fileId}.json`,
    {
      body: JSON.stringify({ project_file: data }),
      responseKind: "json",
      headers: { "Content-Type": "application/json" }
    }
  );
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to update file");
  }
  return response.json;
}

/**
 * Get project links from project_links resource.
 */
export async function getProjectLinks(siteSlug: string, projectId: number) {
  const response = await fetch(`/sites/${siteSlug}/projects/${projectId}/project_links.json`, {
    credentials: "include",
    headers: { "Accept": "application/json" }
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to fetch project links");
  }
  return response.json();
}

/**
 * Add a link to a project.
 */
export async function addProjectLink(
  siteSlug: string,
  projectId: number,
  linkData: { url: string; link_type: string; title?: string }
) {
  const response = await post(`/sites/${siteSlug}/projects/${projectId}/project_links.json`, {
    body: JSON.stringify({ project_link: linkData }),
    responseKind: "json",
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to add link");
  }
  return response.json;
}

/**
 * Remove a link from a project.
 */
export async function removeProjectLink(siteSlug: string, projectId: number, linkId: number) {
  const response = await destroy(`/sites/${siteSlug}/projects/${projectId}/project_links/${linkId}.json`, {
    responseKind: "json",
  });

  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to delete link");
  }
  return response.json;
}

/**
 * Update a project link.
 */
export async function updateProjectLink(
  siteSlug: string,
  projectId: number,
  linkId: number,
  linkData: { url?: string; link_type?: string; title?: string }
) {
  const response = await patch(
    `/sites/${siteSlug}/projects/${projectId}/project_links/${linkId}.json`,
    {
      body: JSON.stringify({ project_link: linkData }),
      responseKind: "json",
      headers: { "Content-Type": "application/json" }
    }
  );
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to update link");
  }
  return response.json;
}

/**
 * Update a project with any data.
 */
export async function updateProject(siteSlug: string, projectId: number | string, data: any) {
  const response = await patch(`/sites/${siteSlug}/projects/${projectId}.json`, {
    body: JSON.stringify({ project: data }),
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to update project");
  }
  return response.json;
}

/**
 * Products API
 */

/**
 * Fetch all products for a site.
 */
export async function getProducts(siteSlug: string): Promise<any[]> {
  const response = await get(`/sites/${siteSlug}/products.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch products");
  }
  return await response.json;
}

/**
 * Fetch a single product.
 */
export async function getProduct(siteSlug: string, productId: number | string): Promise<any> {
  const response = await get(`/sites/${siteSlug}/products/${productId}.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch product");
  }
  return await response.json;
}

/**
 * Create a new product.
 */
export async function createProduct(siteSlug: string, productData: any) {
  const response = await post(`/sites/${siteSlug}/products.json`, {
    body: JSON.stringify({ product: productData }),
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to create product");
  }
  return response.json;
}

/**
 * Update a product.
 */
export async function updateProduct(siteSlug: string, productId: number | string, productData: any) {
  const response = await patch(`/sites/${siteSlug}/products/${productId}.json`, {
    body: JSON.stringify({ product: productData }),
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to update product");
  }
  return response.json;
}

/**
 * Delete a product.
 */
export async function deleteProduct(siteSlug: string, productId: number | string) {
  const response = await destroy(`/sites/${siteSlug}/products/${productId}.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to delete product");
  }
  return response.json;
}

/**
 * Publish a product.
 */
export async function publishProduct(siteSlug: string, productId: number | string) {
  const response = await patch(`/sites/${siteSlug}/products/${productId}/publish.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to publish product");
  }
  return response.json;
}

/**
 * Archive a product.
 */
export async function archiveProduct(siteSlug: string, productId: number | string) {
  const response = await patch(`/sites/${siteSlug}/products/${productId}/archive.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to archive product");
  }
  return response.json;
}

/**
 * Product Files API
 */

/**
 * Get all files for a product.
 */
export async function getProductFiles(siteSlug: string, productId: number | string): Promise<any[]> {
  const response = await get(`/sites/${siteSlug}/products/${productId}/product_files.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch product files");
  }
  return await response.json;
}

/**
 * Upload a file for a product.
 */
export async function uploadProductFile(
  siteSlug: string,
  productId: number | string,
  signedBlobId: string,
  title: string,
  description: string,
  fileType: string
) {
  const response = await post(`/sites/${siteSlug}/products/${productId}/product_files.json`, {
    body: JSON.stringify({
      product_file: { title, description, file_type: fileType },
      signed_blob_id: signedBlobId,
    }),
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to upload file");
  }
  return response.json;
}

/**
 * Delete a product file.
 */
export async function deleteProductFile(siteSlug: string, productId: number | string, fileId: number) {
  const response = await destroy(`/sites/${siteSlug}/products/${productId}/product_files/${fileId}.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to delete file");
  }
  return response.json;
}

/**
 * Product Links API
 */

/**
 * Get all links for a product.
 */
export async function getProductLinks(siteSlug: string, productId: number | string): Promise<any[]> {
  const response = await get(`/sites/${siteSlug}/products/${productId}/product_links.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.error || "Failed to fetch product links");
  }
  return await response.json;
}

/**
 * Add a link for a product.
 */
export async function addProductLink(
  siteSlug: string,
  productId: number | string,
  linkData: { url: string; link_type: string; title?: string }
) {
  const response = await post(`/sites/${siteSlug}/products/${productId}/product_links.json`, {
    body: JSON.stringify({ product_link: linkData }),
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to add link");
  }
  return response.json;
}

/**
 * Remove a product link.
 */
export async function removeProductLink(siteSlug: string, productId: number | string, linkId: number) {
  const response = await destroy(`/sites/${siteSlug}/products/${productId}/product_links/${linkId}.json`, {
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to remove link");
  }
  return response.json;
}
